import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiAlertTriangle, FiCheck, FiPlus, FiSave, FiTrash2, FiX } from "react-icons/fi";
import SectionHeader from "../../../components/sectionHeader/sectionHeader";
import Tabs from "../../../components/tabs/tabs";
import { OBRAS_CAMPOS_PERMISOS } from "../obras/obras";
import {
    PERFILES, VALORES_ESPECIALES, VISTAS_GRUPOS, aPeticion, alternarCampos, conModoEdicion, esAdministradorAtlante,
    filtrarGrupos, gruposDeConfiguracion, gruposSinAcceso, modoEdicion, nuevaConfiguracion, ponerFiltro,
    resumenConfiguracion, textoValor, tieneCambios, tiposDeEmpresa, validarConfiguracion,
} from "./permisosObrasModel";
import {
    asignarGrupo, borrarConfiguracion, fetchConfiguraciones, fetchDimensiones, fetchGrupos, guardarConfiguracion, mensajeError,
} from "./permisosObrasService";

import "./permisosObras.scss";

const claveGrupo = (grupo) => `${grupo.codEmpresa}-${grupo.codPerfil}`;
const formatoFecha = (valor) => {
    if (!valor) return "";
    const fecha = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(valor) ? valor : `${valor}Z`);
    return Number.isNaN(fecha.getTime()) ? "" : fecha.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" });
};

// Panel de Administrador → Gestión de permisos obras. Cada grupo (empresa + perfil) hereda la
// configuración que tiene asignada: qué obras ve, qué edita y qué acciones hace.
export default function PermisosObras() {
    const [tab, setTab] = useState("grupos");
    const [datos, setDatos] = useState({ cargando: true, error: "", configuraciones: [], grupos: [], dimensiones: [] });
    const [seleccion, setSeleccion] = useState(null);

    const cargar = useCallback(async () => {
        setDatos((actual) => ({ ...actual, cargando: true, error: "" }));
        try {
            const [configuraciones, grupos, dimensiones] = await Promise.all([fetchConfiguraciones(), fetchGrupos(), fetchDimensiones()]);
            setDatos({ cargando: false, error: "", configuraciones, grupos, dimensiones });
        } catch (error) {
            setDatos((actual) => ({ ...actual, cargando: false, error: mensajeError(error, "No se pudieron cargar los permisos de Obras.") }));
        }
    }, []);

    useEffect(() => { cargar(); }, [cargar]);

    const recargarConfiguraciones = async () => {
        const [configuraciones, grupos] = await Promise.all([fetchConfiguraciones(), fetchGrupos()]);
        setDatos((actual) => ({ ...actual, configuraciones, grupos }));
    };

    const abrirConfiguracion = (codConfiguracion) => {
        setSeleccion(codConfiguracion);
        setTab("configuraciones");
    };

    return (
        <section className="permisos-obras">
            <SectionHeader
                title="Panel de Administrador - Gestión de permisos obras"
                subtitle="Qué obras ve cada grupo de usuarios (empresa y perfil), qué puede editar y qué acciones puede hacer"
            />
            <Tabs
                tabs={[{ id: "grupos", label: "Grupos" }, { id: "configuraciones", label: "Configuraciones" }]}
                activeTab={tab}
                onTabChange={setTab}
            />
            {datos.cargando && !datos.configuraciones.length ? (
                <p className="permisos-obras__estado" role="status">Cargando permisos…</p>
            ) : datos.error ? (
                <div className="permisos-obras__aviso permisos-obras__aviso--error" role="alert">
                    <FiAlertTriangle aria-hidden="true" />
                    <span>{datos.error}</span>
                    <button type="button" className="permisos-obras__boton" onClick={cargar}>Reintentar</button>
                </div>
            ) : tab === "grupos" ? (
                <Grupos
                    grupos={datos.grupos}
                    configuraciones={datos.configuraciones}
                    onAsignado={(grupo, codConfiguracion) => setDatos((actual) => ({
                        ...actual,
                        grupos: actual.grupos.map((item) => (claveGrupo(item) === claveGrupo(grupo) ? { ...item, codConfiguracion, fechaModificacion: new Date().toISOString() } : item)),
                        configuraciones: actual.configuraciones.map((c) => ({
                            ...c,
                            grupos: c.grupos + (c.codConfiguracion === codConfiguracion ? 1 : 0) - (c.codConfiguracion === grupo.codConfiguracion ? 1 : 0),
                        })),
                    }))}
                    onAbrirConfiguracion={abrirConfiguracion}
                />
            ) : (
                <Configuraciones
                    configuraciones={datos.configuraciones}
                    grupos={datos.grupos}
                    dimensiones={datos.dimensiones}
                    seleccion={seleccion}
                    onSeleccion={setSeleccion}
                    onCambio={recargarConfiguraciones}
                />
            )}
        </section>
    );
}

function Grupos({ grupos, configuraciones, onAsignado, onAbrirConfiguracion }) {
    const [texto, setTexto] = useState("");
    const [vista, setVista] = useState("relevantes");
    const [tipo, setTipo] = useState("");
    const [filas, setFilas] = useState({});
    const visibles = useMemo(() => filtrarGrupos(grupos, { texto, vista, tipo }), [grupos, texto, vista, tipo]);
    const sinAcceso = useMemo(() => gruposSinAcceso(grupos), [grupos]);
    const tipos = useMemo(() => tiposDeEmpresa(grupos), [grupos]);
    const nombreDe = useMemo(() => Object.fromEntries(configuraciones.map((c) => [c.codConfiguracion, c.nombre])), [configuraciones]);

    const asignar = async (grupo, valor) => {
        const codConfiguracion = valor === "" ? null : Number(valor);
        const clave = claveGrupo(grupo);
        setFilas((actual) => ({ ...actual, [clave]: { estado: "guardando" } }));
        try {
            await asignarGrupo(grupo.codEmpresa, grupo.codPerfil, codConfiguracion);
            onAsignado(grupo, codConfiguracion);
            setFilas((actual) => ({ ...actual, [clave]: { estado: "ok" } }));
        } catch (error) {
            setFilas((actual) => ({ ...actual, [clave]: { estado: "error", mensaje: mensajeError(error, "No se pudo guardar.") } }));
        }
    };

    return (
        <div className="permisos-obras__panel">
            {sinAcceso.length ? (
                <div className="permisos-obras__aviso" role="status">
                    <FiAlertTriangle aria-hidden="true" />
                    <span>
                        {sinAcceso.length === 1 ? "Hay 1 grupo con usuarios" : `Hay ${sinAcceso.length} grupos con usuarios`} y sin configuración: esos usuarios no entran en Obras.
                    </span>
                    <button type="button" className="permisos-obras__boton" onClick={() => { setVista("sinAcceso"); setTipo(""); setTexto(""); }}>Ver grupos</button>
                </div>
            ) : null}

            <div className="permisos-obras__barra">
                <label className="permisos-obras__campo permisos-obras__campo--buscar">
                    <span>Buscar empresa</span>
                    <input type="search" value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Nombre o tipo de empresa" />
                </label>
                <label className="permisos-obras__campo">
                    <span>Mostrar</span>
                    <select value={vista} onChange={(e) => setVista(e.target.value)}>
                        {VISTAS_GRUPOS.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
                    </select>
                </label>
                <label className="permisos-obras__campo">
                    <span>Tipo de empresa</span>
                    <select value={tipo} onChange={(e) => setTipo(e.target.value)}>
                        <option value="">Todos</option>
                        {tipos.map((t) => <option key={t.cod} value={t.cod}>{t.nombre}</option>)}
                    </select>
                </label>
                <span className="permisos-obras__recuento">{visibles.length} {visibles.length === 1 ? "grupo" : "grupos"}</span>
            </div>

            {visibles.length ? (
                <div className="permisos-obras__tabla-wrap">
                    <table className="permisos-obras__tabla">
                        <thead>
                            <tr>
                                <th scope="col">Empresa</th>
                                <th scope="col">Tipo</th>
                                <th scope="col">Perfil</th>
                                <th scope="col" className="num">Usuarios</th>
                                <th scope="col">Configuración</th>
                                <th scope="col">Modificado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibles.map((grupo) => {
                                const clave = claveGrupo(grupo);
                                const fila = filas[clave];
                                const fija = esAdministradorAtlante(grupo);
                                return (
                                    <tr key={clave} className={!fija && grupo.codConfiguracion == null ? "sin-acceso" : ""}>
                                        <td>{grupo.empresa || `Empresa ${grupo.codEmpresa}`}</td>
                                        <td>{grupo.tipoEmpresa || "—"}</td>
                                        <td>{PERFILES[grupo.codPerfil]}</td>
                                        <td className="num">{grupo.usuarios}</td>
                                        <td>
                                            {fija ? (
                                                <span className="permisos-obras__fija" title="Los administradores de Grupo Atlante tienen siempre acceso total y gestionan los permisos.">Acceso total (fijo)</span>
                                            ) : (
                                                <div className="permisos-obras__asignar">
                                                    <select
                                                        aria-label={`Configuración de ${grupo.empresa}, ${PERFILES[grupo.codPerfil]}`}
                                                        value={grupo.codConfiguracion ?? ""}
                                                        disabled={fila?.estado === "guardando"}
                                                        onChange={(e) => asignar(grupo, e.target.value)}
                                                    >
                                                        <option value="">Sin acceso</option>
                                                        {configuraciones.map((c) => <option key={c.codConfiguracion} value={c.codConfiguracion}>{c.nombre}</option>)}
                                                    </select>
                                                    {grupo.codConfiguracion != null && nombreDe[grupo.codConfiguracion] ? (
                                                        <button type="button" className="permisos-obras__enlace" onClick={() => onAbrirConfiguracion(grupo.codConfiguracion)}>Ver</button>
                                                    ) : null}
                                                    {fila?.estado === "guardando" ? <span className="permisos-obras__fila-estado" role="status">Guardando…</span> : null}
                                                    {fila?.estado === "ok" ? <span className="permisos-obras__fila-estado ok" role="status"><FiCheck aria-hidden="true" /> Guardado</span> : null}
                                                    {fila?.estado === "error" ? <span className="permisos-obras__fila-estado error" role="alert">{fila.mensaje}</span> : null}
                                                </div>
                                            )}
                                        </td>
                                        <td className="permisos-obras__fecha">{fija ? "" : formatoFecha(grupo.fechaModificacion)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ) : (
                <p className="permisos-obras__estado">Ningún grupo coincide con la búsqueda.</p>
            )}
        </div>
    );
}

function Configuraciones({ configuraciones, grupos, dimensiones, seleccion, onSeleccion, onCambio }) {
    const guardada = configuraciones.find((c) => c.codConfiguracion === seleccion) || null;
    const [borrador, setBorrador] = useState(() => (guardada ? { ...guardada } : null));
    const [estado, setEstado] = useState({ guardando: false, error: "", aviso: "" });
    // Confirmación pendiente (descartar cambios, borrar): diálogo de la página, nunca el del navegador.
    const [confirmacion, setConfirmacion] = useState(null);
    const cargadaRef = useRef(seleccion);

    // Al elegir otra configuración (o al recargarse la lista), el borrador parte de la guardada.
    useEffect(() => {
        const otra = cargadaRef.current !== seleccion;
        if (otra || !borrador || (guardada && borrador.codConfiguracion !== guardada.codConfiguracion)) {
            cargadaRef.current = seleccion;
            setBorrador(guardada ? { ...guardada } : seleccion === 0 ? nuevaConfiguracion() : null);
            // Recién creada (de 0 a su código) se conserva el aviso de guardado.
            if (otra) setEstado({ guardando: false, error: "", aviso: "" });
        }
    }, [seleccion, guardada]); // eslint-disable-line react-hooks/exhaustive-deps

    const sucio = borrador ? tieneCambios(borrador, guardada) : false;

    const elegir = (codConfiguracion) => {
        if (codConfiguracion === seleccion) return;
        if (!sucio) { onSeleccion(codConfiguracion); return; }
        setConfirmacion({
            titulo: "Cambios sin guardar",
            mensaje: `Tienes cambios sin guardar en «${borrador.nombre || "esta configuración"}». Si sigues, se pierden.`,
            confirmar: "Descartar cambios",
            cancelar: "Seguir editando",
            alAceptar: () => onSeleccion(codConfiguracion),
        });
    };

    const guardar = async () => {
        const error = validarConfiguracion(borrador);
        if (error) { setEstado({ guardando: false, error, aviso: "" }); return; }
        setEstado({ guardando: true, error: "", aviso: "" });
        try {
            const cod = await guardarConfiguracion(borrador.codConfiguracion, aPeticion(borrador));
            await onCambio();
            cargadaRef.current = cod;
            onSeleccion(cod);
            setEstado({ guardando: false, error: "", aviso: "Guardada. Se aplica a sus usuarios en menos de un minuto." });
        } catch (e) {
            setEstado({ guardando: false, error: mensajeError(e, "No se pudo guardar la configuración."), aviso: "" });
        }
    };

    const borrar = () => setConfirmacion({
        titulo: "Borrar configuración",
        mensaje: `Se borra «${guardada.nombre}». No está asignada a ningún grupo, así que nadie pierde el acceso.`,
        confirmar: "Borrar",
        cancelar: "Cancelar",
        peligro: true,
        alAceptar: borrarConfirmado,
    });

    const borrarConfirmado = async () => {
        setEstado({ guardando: true, error: "", aviso: "" });
        try {
            await borrarConfiguracion(guardada.codConfiguracion);
            await onCambio();
            onSeleccion(null);
        } catch (e) {
            setEstado({ guardando: false, error: mensajeError(e, "No se pudo borrar la configuración."), aviso: "" });
        }
    };

    const asignada = guardada ? gruposDeConfiguracion(grupos, guardada.codConfiguracion) : [];

    return (
        <div className="permisos-obras__config">
            {confirmacion ? (
                <Confirmacion
                    {...confirmacion}
                    onAceptar={() => { const accion = confirmacion.alAceptar; setConfirmacion(null); accion(); }}
                    onCancelar={() => setConfirmacion(null)}
                />
            ) : null}
            <nav className="permisos-obras__lista" aria-label="Configuraciones">
                <button type="button" className="permisos-obras__nueva" onClick={() => elegir(0)}>
                    <FiPlus aria-hidden="true" /> Nueva configuración
                </button>
                <ul>
                    {configuraciones.map((c) => {
                        const resumen = resumenConfiguracion(c, dimensiones);
                        return (
                            <li key={c.codConfiguracion}>
                                <button
                                    type="button"
                                    className={`permisos-obras__item${c.codConfiguracion === seleccion ? " activa" : ""}`}
                                    aria-current={c.codConfiguracion === seleccion ? "true" : undefined}
                                    onClick={() => elegir(c.codConfiguracion)}
                                >
                                    <strong>{c.nombre}</strong>
                                    <span>Ve: {resumen.ve}</span>
                                    <span>Edita: {resumen.edita}</span>
                                    <small>{c.grupos} {c.grupos === 1 ? "grupo" : "grupos"}</small>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            </nav>

            {borrador ? (
                <form className="permisos-obras__editor" onSubmit={(e) => { e.preventDefault(); guardar(); }}>
                    <header className="permisos-obras__editor-cabecera">
                        <h3>{borrador.codConfiguracion ? borrador.nombre || "Sin nombre" : "Nueva configuración"}</h3>
                        {asignada.length ? (
                            <p>Asignada a {asignada.map((g) => `${g.empresa} (${PERFILES[g.codPerfil]})`).join(", ")}.</p>
                        ) : borrador.codConfiguracion ? <p>No está asignada a ningún grupo.</p> : null}
                    </header>

                    <fieldset className="permisos-obras__bloque">
                        <legend>Datos</legend>
                        <label className="permisos-obras__campo">
                            <span>Nombre</span>
                            <input value={borrador.nombre} maxLength={80} onChange={(e) => setBorrador({ ...borrador, nombre: e.target.value })} required />
                        </label>
                        <label className="permisos-obras__campo">
                            <span>Descripción</span>
                            <input value={borrador.descripcion || ""} maxLength={300} onChange={(e) => setBorrador({ ...borrador, descripcion: e.target.value })} placeholder="Para quién es y qué permite" />
                        </label>
                    </fieldset>

                    <FiltroEditor borrador={borrador} dimensiones={dimensiones} onChange={setBorrador} />
                    <CamposEditor borrador={borrador} onChange={setBorrador} />

                    <fieldset className="permisos-obras__bloque">
                        <legend>Acciones</legend>
                        {[
                            ["puedeCrear", "Crear y duplicar obras", "El alta da valor a cualquier campo de la obra."],
                            ["puedeArchivar", "Archivar y reactivar obras", null],
                            ["puedeCatalogos", "Gestionar catálogos", "Crear, renombrar, activar o borrar valores de las listas (también países y ubicaciones)."],
                        ].map(([clave, texto, ayuda]) => (
                            <label key={clave} className="permisos-obras__check">
                                <input type="checkbox" checked={borrador[clave]} onChange={(e) => setBorrador({ ...borrador, [clave]: e.target.checked })} />
                                <span>{texto}{ayuda ? <small>{ayuda}</small> : null}</span>
                            </label>
                        ))}
                    </fieldset>

                    <footer className="permisos-obras__pie">
                        {estado.error ? <p className="permisos-obras__error" role="alert"><FiAlertTriangle aria-hidden="true" /> {estado.error}</p> : null}
                        {estado.aviso && !sucio ? <p className="permisos-obras__ok" role="status"><FiCheck aria-hidden="true" /> {estado.aviso}</p> : null}
                        <div className="permisos-obras__acciones">
                            {guardada ? (
                                <button
                                    type="button"
                                    className="permisos-obras__boton permisos-obras__boton--peligro"
                                    onClick={borrar}
                                    disabled={estado.guardando || guardada.grupos > 0}
                                    title={guardada.grupos > 0 ? "Está asignada a algún grupo: asígnales otra antes de borrarla." : undefined}
                                >
                                    <FiTrash2 aria-hidden="true" /> Borrar
                                </button>
                            ) : null}
                            <span className="permisos-obras__hueco" />
                            {sucio ? (
                                <button type="button" className="permisos-obras__boton" onClick={() => guardada ? setBorrador({ ...guardada }) : onSeleccion(null)} disabled={estado.guardando}>
                                    Descartar cambios
                                </button>
                            ) : null}
                            <button type="submit" className="permisos-obras__boton permisos-obras__boton--primario" disabled={estado.guardando || !sucio}>
                                <FiSave aria-hidden="true" /> {estado.guardando ? "Guardando…" : "Guardar"}
                            </button>
                        </div>
                    </footer>
                </form>
            ) : (
                <div className="permisos-obras__vacio">
                    <p>Elige una configuración para verla o cambiarla, o crea una nueva.</p>
                    <p>Una configuración se asigna a grupos (empresa + perfil) en la pestaña Grupos; todos sus usuarios la heredan.</p>
                </div>
            )}
        </div>
    );
}

// Diálogo de confirmación de la página. Escape o pulsar fuera cancelan.
function Confirmacion({ titulo, mensaje, confirmar, cancelar, peligro = false, onAceptar, onCancelar }) {
    useEffect(() => {
        const alPulsar = (event) => { if (event.key === "Escape") onCancelar(); };
        document.addEventListener("keydown", alPulsar);
        return () => document.removeEventListener("keydown", alPulsar);
    }, [onCancelar]);
    return (
        <div className="permisos-obras__capa" role="presentation" onMouseDown={onCancelar}>
            <div
                className={`permisos-obras__dialogo${peligro ? " permisos-obras__dialogo--peligro" : ""}`}
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="permisos-obras-dialogo-titulo"
                aria-describedby="permisos-obras-dialogo-mensaje"
                onMouseDown={(event) => event.stopPropagation()}
            >
                <h3 id="permisos-obras-dialogo-titulo">{titulo}</h3>
                <p id="permisos-obras-dialogo-mensaje">{mensaje}</p>
                <div className="permisos-obras__dialogo-acciones">
                    <button type="button" className="permisos-obras__boton" onClick={onCancelar}>{cancelar}</button>
                    <button type="button" className={`permisos-obras__boton ${peligro ? "permisos-obras__boton--peligro-lleno" : "permisos-obras__boton--primario"}`} onClick={onAceptar} autoFocus>
                        {confirmar}
                    </button>
                </div>
            </div>
        </div>
    );
}

function FiltroEditor({ borrador, dimensiones, onChange }) {
    const usadas = Object.keys(borrador.filtros);
    const porClave = Object.fromEntries(dimensiones.map((d) => [d.clave, d]));
    const libres = dimensiones.filter((d) => !usadas.includes(d.clave));
    const [todas, setTodas] = useState(usadas.length === 0);
    useEffect(() => { setTodas(Object.keys(borrador.filtros).length === 0); }, [borrador.codConfiguracion]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <fieldset className="permisos-obras__bloque">
            <legend>Qué obras ve</legend>
            <div className="permisos-obras__radios">
                <label className="permisos-obras__check">
                    <input type="radio" name="ve" checked={todas && !usadas.length} onChange={() => { setTodas(true); onChange({ ...borrador, filtros: {} }); }} />
                    <span>Todas las obras</span>
                </label>
                <label className="permisos-obras__check">
                    <input type="radio" name="ve" checked={!todas || usadas.length > 0} onChange={() => setTodas(false)} />
                    <span>Solo las que cumplen estas condiciones<small>Dentro de una condición basta con un valor; entre condiciones, hacen falta todas.</small></span>
                </label>
            </div>
            {!todas || usadas.length ? (
                <div className="permisos-obras__condiciones">
                    {usadas.map((clave) => {
                        const dimension = porClave[clave];
                        const valores = borrador.filtros[clave];
                        const elegibles = [
                            ...(dimension?.especial ? [{ valor: dimension.especial, texto: VALORES_ESPECIALES[dimension.especial] }] : []),
                            ...(dimension?.opciones || []).map((o) => ({ ...o, texto: o.activo === false ? `${o.texto} (inactivo)` : o.texto })),
                        ].filter((o) => !valores.includes(o.valor));
                        return (
                            <div key={clave} className="permisos-obras__condicion">
                                <strong>{dimension?.etiqueta || clave}</strong>
                                <div className="permisos-obras__chips">
                                    {valores.map((valor) => (
                                        <span key={valor} className={`permisos-obras__chip${VALORES_ESPECIALES[valor] ? " especial" : ""}`}>
                                            {textoValor(dimension, valor)}
                                            <button type="button" aria-label={`Quitar ${textoValor(dimension, valor)}`} onClick={() => onChange(ponerFiltro(borrador, clave, valores.filter((v) => v !== valor)))}>
                                                <FiX aria-hidden="true" />
                                            </button>
                                        </span>
                                    ))}
                                    <select
                                        aria-label={`Añadir valor a ${dimension?.etiqueta || clave}`}
                                        value=""
                                        onChange={(e) => e.target.value && onChange(ponerFiltro(borrador, clave, [...valores, e.target.value]))}
                                    >
                                        <option value="">{valores.length ? "Añadir otro valor…" : "Elegir valor…"}</option>
                                        {elegibles.map((o) => <option key={o.valor} value={o.valor}>{o.texto}</option>)}
                                    </select>
                                </div>
                                <button type="button" className="permisos-obras__enlace" onClick={() => onChange(ponerFiltro(borrador, clave, undefined))}>Quitar condición</button>
                            </div>
                        );
                    })}
                    {libres.length ? (
                        <label className="permisos-obras__campo permisos-obras__campo--condicion">
                            <span>Añadir condición</span>
                            <select value="" onChange={(e) => e.target.value && onChange(ponerFiltro(borrador, e.target.value, []))}>
                                <option value="">Elegir campo de la obra…</option>
                                {libres.map((d) => <option key={d.clave} value={d.clave}>{d.etiqueta}</option>)}
                            </select>
                        </label>
                    ) : null}
                </div>
            ) : null}
        </fieldset>
    );
}

function CamposEditor({ borrador, onChange }) {
    const modo = modoEdicion(borrador);
    const marcados = new Set(borrador.campos);
    const pestanas = [...new Set(OBRAS_CAMPOS_PERMISOS.map((s) => s.tab))];

    return (
        <fieldset className="permisos-obras__bloque">
            <legend>Qué puede editar</legend>
            <div className="permisos-obras__radios">
                {[["nada", "Nada (solo consulta)"], ["todo", "Todos los campos"], ["campos", "Solo estos campos"]].map(([id, texto]) => (
                    <label key={id} className="permisos-obras__check">
                        <input type="radio" name="edita" checked={modo === id} onChange={() => onChange(conModoEdicion(borrador, id))} />
                        <span>{texto}</span>
                    </label>
                ))}
            </div>
            {modo === "campos" ? (
                <div className="permisos-obras__campos">
                    {pestanas.map((pestana) => (
                        <div key={pestana} className="permisos-obras__pestana">
                            <h4>{pestana}</h4>
                            {OBRAS_CAMPOS_PERMISOS.filter((s) => s.tab === pestana).map((seccion) => {
                                const claves = seccion.campos.map((c) => c.clave);
                                const cuantos = claves.filter((c) => marcados.has(c)).length;
                                return (
                                    <div key={seccion.id} className="permisos-obras__seccion">
                                        <label className="permisos-obras__check permisos-obras__check--seccion">
                                            <input
                                                type="checkbox"
                                                checked={cuantos === claves.length}
                                                ref={(el) => { if (el) el.indeterminate = cuantos > 0 && cuantos < claves.length; }}
                                                onChange={(e) => onChange(alternarCampos(borrador, claves, e.target.checked))}
                                            />
                                            <span>{seccion.label}</span>
                                        </label>
                                        <div className="permisos-obras__seccion-campos">
                                            {seccion.campos.map((campo) => (
                                                <label key={campo.clave} className="permisos-obras__check">
                                                    <input type="checkbox" checked={marcados.has(campo.clave)} onChange={(e) => onChange(alternarCampos(borrador, [campo.clave], e.target.checked))} />
                                                    <span>{campo.label}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                    <p className="permisos-obras__nota">Un hito incluye su estado, su fecha real y sus notas. País y ubicación se editan juntos, y también CAPEX, coste m² ref. y sup. construida.</p>
                </div>
            ) : null}
        </fieldset>
    );
}
